/**
 * 音声案内（Web Speech API / speechSynthesis）。
 * - iOS は最初の発話をユーザー操作内で行わないと以後も鳴らないため、unlock() をタップ時に呼ぶ。
 * - バックグラウンドや画面ロック中は鳴らない（Web の制約）。
 * - iPhone では、音声の出力（特に車のスピーカーや Bluetooth）の準備が間に合わず、読み上げの最初の
 *   2文字ほどが切れる（実機で発生）。市販のカーナビと同じく、先に短い案内音を鳴らして出力を起こしてから読む
 */
/** 案内音を鳴らしてから読み上げを始めるまで [ms] */
const CHIME_LEAD = 380;

export class Speaker {
  private voice: SpeechSynthesisVoice | null = null;
  private audio: AudioContext | null = null;
  private pending = 0;
  unlocked = false;
  enabled = true;
  /** 読み上げの前に案内音を鳴らす */
  chime = true;

  constructor() {
    if (!this.supported) return;
    const pick = () => {
      const voices = speechSynthesis.getVoices();
      this.voice = voices.find((v) => v.lang === 'ja-JP') ?? voices.find((v) => v.lang.startsWith('ja')) ?? null;
    };
    pick();
    speechSynthesis.addEventListener?.('voiceschanged', pick);
  }

  get supported(): boolean {
    return 'speechSynthesis' in window;
  }

  get voiceName(): string {
    return this.voice ? `${this.voice.name} (${this.voice.lang})` : '（日本語音声なし／未取得）';
  }

  /** ユーザー操作（タップ）のハンドラ内から呼ぶこと（iOS は音を出す許可をタップの中でしか取れない） */
  unlock(): void {
    if (!this.supported || this.unlocked) return;
    const u = new SpeechSynthesisUtterance(' ');
    u.volume = 0;
    speechSynthesis.speak(u);
    try {
      this.audio = new AudioContext();
      void this.audio.resume();
    } catch {
      this.audio = null; // Web Audio が無ければ案内音なしで読む
    }
    this.unlocked = true;
  }

  say(text: string, opts: { interrupt?: boolean } = {}): void {
    if (!this.supported || !this.enabled) return;
    if (opts.interrupt) {
      speechSynthesis.cancel();
      clearTimeout(this.pending);
    }
    const speak = () => {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = 'ja-JP';
      if (this.voice) u.voice = this.voice;
      u.rate = 1.05;
      speechSynthesis.speak(u);
    };
    if (this.chime && this.playChime()) this.pending = window.setTimeout(speak, CHIME_LEAD);
    else speak();
  }

  /** 「ポーン」（2音・約0.3秒）。鳴らせたら true */
  private playChime(): boolean {
    const ctx = this.audio;
    if (!ctx) return false;
    if (ctx.state !== 'running') void ctx.resume();
    const t0 = ctx.currentTime + 0.02;
    const tone = (freq: number, at: number, len: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(0.25, at + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, at + len);
      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + len + 0.02);
    };
    tone(988, t0, 0.18); // シ
    tone(1319, t0 + 0.12, 0.25); // ミ
    return true;
  }
}
